// Plain-language, layman explanations for every metric surfaced in the app.
// Single source of truth so the "i" info tooltips read consistently everywhere.
// Keep these jargon-free and honest (see docs/RULES.md §6–7 for the real defs).

export interface MetricInfo {
  title: string;
  text: string;
}

export const METRIC_INFO = {
  // ── Meta performance (Overview / Campaigns) ───────────────────────────────
  spend: {
    title: 'Spend',
    text: 'How much money the ads have cost so far in this period. This is what Meta charged, not what it earned back.',
  },
  impressions: {
    title: 'Impressions',
    text: 'How many times the ads were shown on screen. One person can see them several times, so this counts views, not people.',
  },
  reach: {
    title: 'Reach',
    text: 'How many different people saw the ads at least once — the real audience size, without double-counting repeat views.',
  },
  clicks: {
    title: 'Clicks',
    text: 'Two numbers. The big one is link clicks — people who actually opened the website. The small "all" figure underneath adds every other tap: likes, comments, shares, the profile name, expanding the image. Judge traffic on the big number; the gap between them shows how much attention went to the post rather than the site.',
  },
  linkClicks: {
    title: 'Link clicks',
    text: 'Clicks that specifically opened the website link (not likes, comments, or expanding the ad). The truest measure of traffic sent to the site.',
  },
  ctr: {
    title: 'CTR — Click-Through Rate',
    text: 'Of everyone who saw the ad, the share who clicked through to the site. The small "all" figure underneath counts every kind of tap, so it always looks higher. Judge the creative on the big number: 1–2% is normal, 3%+ is strong.',
  },
  cpc: {
    title: 'CPC — Cost Per Click',
    text: 'The average cost of one visit to the site. The small "all" figure underneath spreads the same spend over every tap, so it always looks cheaper than the traffic really is. Lower is better on the big number.',
  },
  frequency: {
    title: 'Frequency',
    text: 'On average, how many times each person has seen the ad. Above ~3 with a falling click rate means fatigue — time to refresh the creative, not kill it.',
  },
  hook: {
    title: 'Hook Rate',
    text: 'For video ads: the share of people who watched the first 3 seconds. It shows whether the opening stops the scroll. Aim for 25%+ (30–45% is strong) — the single most important signal for getting shown.',
  },
  hold: {
    title: 'Hold Rate',
    text: 'Of those the video hooked, the share who kept watching to the end. It shows whether the ad holds attention, not just grabs it. Aim for ~30%+.',
  },
  status: {
    title: 'Delivery status',
    text: 'Whether the campaign is currently Active, Paused, or still Learning. "Learning" means Meta is still figuring out who to show it to (the first ~7 days) — leave it alone during this window.',
  },

  // ── Bookings / revenue (Cloudbeds) ────────────────────────────────────────
  realizedRevenue: {
    title: 'Realized revenue',
    text: 'The total value of confirmed bookings (checked-in, checked-out, or confirmed) from Cloudbeds. Cancellations and no-shows are excluded, so this is money actually committed — not just clicks.',
  },
  bookingsCount: {
    title: 'Bookings',
    text: 'The number of confirmed reservations in this period. Pending and cancelled ones are not counted.',
  },
  avgBooking: {
    title: 'Average booking value',
    text: 'Realized revenue divided by the number of bookings — the typical amount a guest spends per reservation.',
  },
  codedRevenue: {
    title: 'Revenue with a code',
    text: 'The share of revenue from bookings that used an offer code (e.g. Phuket30). Because every ad carries a code, this is the part of revenue we can trace back to a campaign/offer.',
  },
  revenueByCode: {
    title: 'Revenue by offer code',
    text: 'Booking revenue grouped by the promo code guests used. Each code belongs to a campaign or offer, so this is the closest thing to "which ad made money" — real ROAS without tracking pixels. "No code" bookings came in without one.',
  },
  revenueByChannel: {
    title: 'Revenue by channel',
    text: 'How bookings reached you: your Website/Booking Engine, Phone, or Walk-In. Note: website bookings mix ad-driven and organic visitors, so this is the route in — not proof the ad caused it.',
  },
  revenueOverTime: {
    title: 'Revenue over time',
    text: 'Confirmed booking revenue by check-in month — shows the trend and seasonality of when guests are actually staying.',
  },
  bookingStatus: {
    title: 'Booking status',
    text: 'The mix of reservation states: confirmed/checked-in/out count as revenue; pending (not yet confirmed) and cancelled do not.',
  },
  leadTime: {
    title: 'Lead time',
    text: 'The average number of days between when a guest books and when they check in. Longer lead times mean people plan trips well ahead.',
  },
  nights: {
    title: 'Average nights',
    text: 'The typical length of stay per booking, in nights.',
  },
} satisfies Record<string, MetricInfo>;
